import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Progress,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from "antd";
import {
  ApartmentOutlined,
  BankOutlined,
  BookOutlined,
  DollarOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useLocation, useNavigate } from "react-router-dom";
import useAcademicYears from "../hooks/common/useAcademicYears";
import useTerms from "../hooks/common/useTerms";
import Utilities from "../dependencies/custom/react-utilities";
import Settings from "../dependencies/custom/settings";
import "./Welcome.css";

const { Title, Text } = Typography;

const CHART_COLORS = ["#1677ff", "#52c41a", "#faad14", "#722ed1", "#13c2c2"];

const STATUS_COLORS = {
  DRAFT: "default",
  SUBMITTED: "processing",
  VERIFIED: "blue",
  APPROVED: "success",
  INTERVIEW: "orange",
  ASSESSMENT: "purple",
  ADMITTED: "success",
  REJECTED: "error",
  CANCELLED: "default",
  "WAITING LIST": "warning",
};

const getPersistedUser = () => {
  try {
    const sessionUser = JSON.parse(sessionStorage.getItem("user") || "null");
    if (sessionUser && typeof sessionUser === "object") {
      return sessionUser;
    }
  } catch (error) {
    // Ignore invalid session payloads and fall back to the cross-tab lock.
  }

  const lockUser = Utilities.getSessionLock()?.user;
  return lockUser && typeof lockUser === "object" ? lockUser : null;
};

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("en-UG", { maximumFractionDigits: 0 });

const formatCurrency = (value) => `UGX ${formatNumber(value)}`;

const compactCurrency = (value) => {
  const amount = Number(value || 0);
  if (Math.abs(amount) >= 1_000_000_000) {
    return `${(amount / 1_000_000_000).toFixed(1)}B`;
  }
  if (Math.abs(amount) >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(amount) >= 1_000) {
    return `${(amount / 1_000).toFixed(0)}K`;
  }
  return formatNumber(amount);
};

const getWardName = (ward) =>
  [ward?.first_name, ward?.middle_name, ward?.last_name]
    .filter(Boolean)
    .join(" ") || "Student record";

const SummaryCard = ({ title, value, icon, accent }) => (
  <Card className="welcome-dashboard__metric" bordered={false}>
    <div className="welcome-dashboard__metric-row">
      <div
        className="welcome-dashboard__metric-icon"
        style={{ color: accent, backgroundColor: `${accent}14` }}
        aria-hidden="true"
      >
        {icon}
      </div>
      <Statistic title={title} value={Number(value || 0)} />
    </div>
  </Card>
);

const WardCards = ({ wards = [], applicant = false }) => {
  const title = applicant ? "My applications" : "My children";

  return (
    <section
      aria-labelledby="wards-heading"
      className="welcome-dashboard__section"
    >
      <div className="welcome-dashboard__section-heading">
        <div>
          <Title id="wards-heading" level={4}>
            {title}
          </Title>
          <Text type="secondary">
            {applicant
              ? "Track the current status of your application."
              : "Students linked to your account."}
          </Text>
        </div>
      </div>

      {!wards.length ? (
        <Card bordered={false}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              applicant
                ? "No application record is linked to this account."
                : "No children are linked to this account."
            }
          />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {wards.map((ward, index) => (
            <Col xs={24} md={12} xl={8} key={ward?.student_id || index}>
              <Card className="welcome-dashboard__ward-card" bordered={false}>
                <div className="welcome-dashboard__ward-topline">
                  <div className="welcome-dashboard__avatar" aria-hidden="true">
                    <UserOutlined />
                  </div>
                  <div className="welcome-dashboard__ward-name">
                    <Text strong>{getWardName(ward)}</Text>
                    {ward?.student_id && (
                      <Text type="secondary">ID: {ward.student_id}</Text>
                    )}
                  </div>
                  <Tag
                    color={STATUS_COLORS[ward?.admission_status] || "default"}
                  >
                    {ward?.admission_status || "UNKNOWN"}
                  </Tag>
                </div>
                <div className="welcome-dashboard__ward-details">
                  <div>
                    <Text type="secondary">
                      {applicant ? "Class applied" : "Class"}
                    </Text>
                    <Text strong>
                      {ward?.class_applied || ward?.class || "Not assigned"}
                    </Text>
                  </div>
                  {!applicant && (
                    <div>
                      <Text type="secondary">Stream</Text>
                      <Text strong>{ward?.stream || "Not assigned"}</Text>
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </section>
  );
};

const WelcomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const filterOptionsLoadedRef = useRef(false);
  const [user] = useState(() => location?.state?.user || getPersistedUser());
  const [dashboard, setDashboard] = useState(null);
  const [filters, setFilters] = useState({ academicYear: null, term: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const loadDashboard = useCallback(
    async (nextFilters = {}) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError("");

      const query = new URLSearchParams();
      if (nextFilters.academicYear) {
        query.set("academicYear", nextFilters.academicYear);
      }
      if (nextFilters.term) {
        query.set("term", nextFilters.term);
      }

      const response = await Utilities.fetchWithAuth(
        "GET",
        `${Settings.urls.dashboard.me}${query.toString() ? `?${query}` : ""}`,
        null,
        navigate,
      );

      if (requestId !== requestIdRef.current) return;

      if (response?.ok) {
        setDashboard(response.data);
        setFilters({
          academicYear: response.data?.academic_year || null,
          term: response.data?.term || null,
        });
      } else {
        setError(response?.msg || "Unable to load the dashboard.");
      }
      setLoading(false);
    },
    [navigate],
  );

  useEffect(() => {
    let accessToken = Utilities.getCookie("access");
    let refreshToken = Utilities.getCookie("refresh");

    if (!accessToken && !refreshToken && Utilities.hasActiveSession()) {
      Utilities.restoreSessionToTab();
      accessToken = Utilities.getCookie("access");
      refreshToken = Utilities.getCookie("refresh");
    }

    if (!accessToken && !refreshToken) {
      navigate("../../signin", { replace: true, state: { from: location } });
      return;
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const hasToken =
      Utilities.getCookie("access") || Utilities.getCookie("refresh");
    if (!hasToken) return undefined;

    loadDashboard();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadDashboard]);

  useEffect(() => {
    if (dashboard?.role_type !== "staff" || filterOptionsLoadedRef.current) {
      return;
    }

    filterOptionsLoadedRef.current = true;
    fetchAcademicYears();
    fetchTerms();
  }, [dashboard?.role_type, fetchAcademicYears, fetchTerms]);

  const summaryCards = useMemo(
    () => [
      {
        title: "Students",
        value: dashboard?.summary?.students,
        icon: <TeamOutlined />,
        accent: "#1677ff",
      },
      {
        title: "Class teachers",
        value: dashboard?.summary?.class_teachers,
        icon: <UserOutlined />,
        accent: "#13c2c2",
      },
      {
        title: "Subject teachers",
        value: dashboard?.summary?.subject_teachers,
        icon: <BookOutlined />,
        accent: "#722ed1",
      },
      {
        title: "Classes",
        value: dashboard?.summary?.classes,
        icon: <ApartmentOutlined />,
        accent: "#fa8c16",
      },
      {
        title: "Streams",
        value: dashboard?.summary?.streams,
        icon: <BankOutlined />,
        accent: "#52c41a",
      },
    ],
    [dashboard?.summary],
  );

  const genderData = useMemo(
    () =>
      Object.entries(dashboard?.distribution?.by_gender || {}).map(
        ([name, value]) => ({ name, value: Number(value || 0) }),
      ),
    [dashboard?.distribution?.by_gender],
  );

  const residenceData = useMemo(
    () => [
      { name: "Day", value: Number(dashboard?.distribution?.day || 0) },
      {
        name: "Boarding",
        value: Number(dashboard?.distribution?.boarding || 0),
      },
    ],
    [dashboard?.distribution],
  );

  const financeData = dashboard?.finance
    ? [
        {
          period: `${dashboard.term || "Current term"}`,
          Expected: Number(dashboard.finance.expected || 0),
          Collected: Number(dashboard.finance.collected || 0),
          Outstanding: Number(dashboard.finance.outstanding || 0),
        },
      ]
    : [];

  const collectionRate = dashboard?.finance?.expected
    ? Math.round(
        (Number(dashboard.finance.collected || 0) /
          Number(dashboard.finance.expected)) *
          100,
      )
    : 0;

  const changeFilter = (key, value) => {
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
    loadDashboard(nextFilters);
  };

  const reloadDashboard = () =>
    loadDashboard(dashboard?.role_type === "staff" ? filters : {});

  const hasDistribution = [...genderData, ...residenceData].some(
    (item) => item.value > 0,
  );

  if (!dashboard && loading) {
    return (
      <div className="welcome-dashboard">
        <Card bordered={false}>
          <Skeleton active paragraph={{ rows: 2 }} />
        </Card>
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {[0, 1, 2, 3].map((item) => (
            <Col xs={24} sm={12} lg={6} key={item}>
              <Card bordered={false}>
                <Skeleton active paragraph={false} />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  if (!dashboard && error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Dashboard unavailable"
        description={error}
        action={
          <Button size="small" onClick={reloadDashboard}>
            Try again
          </Button>
        }
      />
    );
  }

  const isStaff = dashboard?.role_type === "staff";
  const isApplicant = dashboard?.role_type === "applicant";

  return (
    <Spin spinning={loading} tip="Updating dashboard...">
      <main className="welcome-dashboard">
        <header className="welcome-dashboard__header">
          <div>
            <Text className="welcome-dashboard__eyebrow">
              {isStaff
                ? "School overview"
                : isApplicant
                  ? "Admissions"
                  : "Family overview"}
            </Text>
            <Title level={2}>Welcome, {user?.fullName || "User"}</Title>
            <Text type="secondary">
              {isStaff
                ? `A quick view of ${dashboard?.academic_year || "the current year"}, ${dashboard?.term || "current term"}.`
                : isApplicant
                  ? "View the latest status of your application."
                  : "View the students connected to your account."}
            </Text>
          </div>

          <Space wrap className="welcome-dashboard__actions">
            {isStaff && (
              <>
                <Select
                  aria-label="Academic year"
                  placeholder="Academic year"
                  value={filters.academicYear}
                  onChange={(value) => changeFilter("academicYear", value)}
                  options={academicYears.map((year) => ({
                    value: String(year.acadyr),
                    label: year.acadyr,
                  }))}
                  style={{ minWidth: 150 }}
                />
                <Select
                  aria-label="Term"
                  placeholder="Term"
                  value={filters.term}
                  onChange={(value) => changeFilter("term", value)}
                  options={terms.map((term) => ({
                    value: String(term.name),
                    label: term.name,
                  }))}
                  style={{ minWidth: 120 }}
                />
              </>
            )}
            <Button icon={<ReloadOutlined />} onClick={reloadDashboard}>
              Refresh
            </Button>
          </Space>
        </header>

        {error && (
          <Alert
            className="welcome-dashboard__alert"
            type="warning"
            showIcon
            message={error}
          />
        )}

        {isStaff && (
          <>
            <section aria-label="School totals">
              <div className="welcome-dashboard__metrics-grid">
                {summaryCards.map((card) => (
                  <SummaryCard key={card.title} {...card} />
                ))}
              </div>
            </section>

            <section
              className="welcome-dashboard__section"
              aria-label="Student distribution"
            >
              <Card
                bordered={false}
                title="Student distribution"
                extra={<Text type="secondary">Admitted students</Text>}
              >
                {!hasDistribution ? (
                  <Empty description="No distribution data is available." />
                ) : (
                  <Row gutter={[24, 24]}>
                    <Col xs={24} lg={12}>
                      <Text strong>By gender</Text>
                      <div className="welcome-dashboard__chart">
                        {genderData.some((item) => item.value > 0) ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={genderData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={48}
                                outerRadius={78}
                                paddingAngle={2}
                              >
                                {genderData.map((entry, index) => (
                                  <Cell
                                    key={entry.name}
                                    fill={
                                      CHART_COLORS[index % CHART_COLORS.length]
                                    }
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(value) => formatNumber(value)}
                              />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </div>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Text strong>Day and boarding</Text>
                      <div className="welcome-dashboard__chart">
                        {residenceData.some((item) => item.value > 0) ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={residenceData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={48}
                                outerRadius={78}
                                paddingAngle={2}
                              >
                                {residenceData.map((entry, index) => (
                                  <Cell
                                    key={entry.name}
                                    fill={
                                      CHART_COLORS[
                                        (index + 2) % CHART_COLORS.length
                                      ]
                                    }
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(value) => formatNumber(value)}
                              />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </div>
                    </Col>
                  </Row>
                )}
              </Card>
            </section>

            {dashboard?.finance && (
              <section
                className="welcome-dashboard__section"
                aria-labelledby="finance-heading"
              >
                <Card
                  bordered={false}
                  title={
                    <Space>
                      <DollarOutlined />
                      <span id="finance-heading">Fee collection</span>
                    </Space>
                  }
                  extra={
                    <Button
                      type="link"
                      onClick={() => navigate("/admin/finance/reports")}
                    >
                      View financial reports
                    </Button>
                  }
                >
                  <Row gutter={[24, 24]}>
                    <Col xs={24} xl={10}>
                      <Row gutter={[12, 16]}>
                        <Col xs={24} sm={8} xl={24}>
                          <Statistic
                            title="Expected"
                            value={Number(dashboard.finance.expected || 0)}
                            formatter={(value) => formatNumber(value)}
                            prefix="UGX"
                          />
                        </Col>
                        <Col xs={24} sm={8} xl={24}>
                          <Statistic
                            title="Collected"
                            value={Number(dashboard.finance.collected || 0)}
                            formatter={(value) => formatNumber(value)}
                            prefix="UGX"
                            valueStyle={{ color: "#389e0d" }}
                          />
                        </Col>
                        <Col xs={24} sm={8} xl={24}>
                          <Statistic
                            title="Outstanding"
                            value={Number(dashboard.finance.outstanding || 0)}
                            formatter={(value) => formatNumber(value)}
                            prefix="UGX"
                            valueStyle={{ color: "#d46b08" }}
                          />
                        </Col>
                      </Row>
                      <div className="welcome-dashboard__progress">
                        <Text type="secondary">Collection progress</Text>
                        <Progress
                          percent={Math.min(Math.max(collectionRate, 0), 100)}
                          format={() => `${collectionRate}%`}
                          status={collectionRate >= 100 ? "success" : "active"}
                        />
                      </div>
                    </Col>
                    <Col xs={24} xl={14}>
                      <div className="welcome-dashboard__finance-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsBarChart
                            data={financeData}
                            margin={{ top: 12, right: 8, left: 8, bottom: 4 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              vertical={false}
                            />
                            <XAxis dataKey="period" />
                            <YAxis tickFormatter={compactCurrency} width={56} />
                            <Tooltip
                              formatter={(value) => formatCurrency(value)}
                            />
                            <Legend />
                            <Bar
                              dataKey="Expected"
                              fill="#1677ff"
                              radius={[4, 4, 0, 0]}
                            />
                            <Bar
                              dataKey="Collected"
                              fill="#52c41a"
                              radius={[4, 4, 0, 0]}
                            />
                            <Bar
                              dataKey="Outstanding"
                              fill="#faad14"
                              radius={[4, 4, 0, 0]}
                            />
                          </RechartsBarChart>
                        </ResponsiveContainer>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </section>
            )}
          </>
        )}

        {Array.isArray(dashboard?.wards) && (
          <WardCards wards={dashboard.wards} applicant={isApplicant} />
        )}
      </main>
    </Spin>
  );
};

export default WelcomePage;
